import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { aboutImage } from "@/data/projects";
import PageHeader from "@/components/layout/PageHeader";
import SectionHeading from "@/components/motion/SectionHeading";
import Reveal from "@/components/motion/Reveal";
import CtaBand from "@/components/layout/CtaBand";

const About = () => {
  const { t } = useLanguage();

  return (
    <>
      <PageHeader chapter={t.about.chapter} title={t.about.title} testid="about-header" />

      <section data-testid="about-story" className="bg-ink py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6 space-y-6">
            <Reveal><p className="text-base sm:text-lg leading-relaxed text-fog">{t.about.p1}</p></Reveal>
            <Reveal delay={0.08}><p className="text-base sm:text-lg leading-relaxed text-fog">{t.about.p2}</p></Reveal>
            <Reveal delay={0.16}>
              <p className="border-l-2 border-brand pl-5 font-display text-xl uppercase tracking-wide text-cream">{t.about.p3}</p>
            </Reveal>
            <Reveal delay={0.22}>
              <Link
                to="/poles"
                data-testid="about-discover-poles-link"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand hover:text-brand-hover"
              >
                {t.about.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="relative border border-line">
                <img
                  src={aboutImage}
                  alt="Équipe OMNIBÂT sur chantier"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute -bottom-5 -left-5 bg-brand px-6 py-4">
                  <p className="font-display text-lg font-bold uppercase tracking-wide text-ink">{t.footer.tagline}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="about-values" className="border-t border-line bg-surface py-20 lg:py-32">
        <div className="absolute" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3">
            <Reveal>
              <div data-testid="about-mission" className="border-t-2 border-brand pt-6">
                <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-cream">{t.about.missionTitle}</h2>
                <p className="mt-3 text-sm leading-relaxed text-fog">{t.about.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div data-testid="about-vision" className="border-t-2 border-brand pt-6">
                <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-cream">{t.about.visionTitle}</h2>
                <p className="mt-3 text-sm leading-relaxed text-fog">{t.about.vision}</p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div data-testid="about-values-list" className="border-t-2 border-brand pt-6">
                <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-cream">{t.about.valuesTitle}</h2>
                <ul className="mt-4 space-y-2.5">
                  {t.about.values.map((v) => (
                    <li key={v} className="flex items-center gap-3 text-sm text-fog">
                      <Check className="h-4 w-4 text-brand shrink-0" /> {v}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default About;
